import { useCallback, useEffect, useMemo } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { compositeRefreshTriggerAtom } from 'pages/type-curves/atoms';
import { gunBarrelStateAtoms } from 'pages/type-curves/inventory-surface/atoms';
import type { InventoryUnitSlotsZone } from 'components/charts/gun-barrel-chart/types';
import type { SlotComposite, SlotCompositeEnd } from 'types/api-responses';
import type { SlotCompositeBody } from 'types/api-payloads';
import { mergeDraftAtom, scopedSlotKeysAtom, type MergeDraft } from './atoms';
import { useUnitBuilder } from './context';
import { useCompositeActions } from './use-composite-wellbores';

/** One LTD of the scoped unit as the merge sees it (from the gun barrel). */
export interface MergeSlot {
  id: number;
  /** Per-lane slot id, `{unit}_{formation}_{n}`. */
  label: string;
  formation: string;
  isPermit: boolean;
  compositeId: number | null;
}

/** A pickable lateral tip: the server's label for display, its key to send. */
export interface MergeEndOption {
  /** `{slotId}|{end}`. */
  key: string;
  slotId: number;
  end: SlotCompositeEnd;
  label: string;
  coordinates: number[];
}

export interface MergeChainRow {
  slotId: number;
  label: string;
  lengthFt: number;
  /** Tangent leading into this leg (none on the first). */
  tangentInFt: number | null;
}

export interface MergeGeometries {
  disabledReason: string | null;
  selectedCount: number;
  otherFormations: string[];
  otherFormationCount: number;
  openMerge: () => void;
  closeMerge: () => void;
  draft: MergeDraft | null;
  landingOptions: MergeEndOption[];
  bottomHoleOptions: MergeEndOption[];
  landingKey: string | null;
  bottomHoleKey: string | null;
  /** Drilling order once both ends are picked and previewed. */
  chain: {
    rows: MergeChainRow[];
    producingLengthFt: number;
    tangentLengthFt: number;
  } | null;
  /** The previewed composite once both ends are picked. */
  previewComposite: SlotComposite | null;
  assignEnd: (role: 'landing' | 'bottomHole', key: string | null) => void;
  createComposite: () => void;
  creating: boolean;
  slotsById: ReadonlyMap<number, MergeSlot>;
}

export const endKey = (slotId: number, end: SlotCompositeEnd): string =>
  `${slotId}|${end}`;

const parseEndKey = (
  key: string
): { slotId: number; end: SlotCompositeEnd } => {
  const [id, end] = key.split('|');
  return { slotId: Number(id), end: end as SlotCompositeEnd };
};

const isPermitSlot = (s: {
  geometrySourceType: string | null;
  hasWell: boolean;
}): boolean => s.geometrySourceType !== 'ltd' && s.hasWell;

const samePoint = (
  a: { slotId: number; end: SlotCompositeEnd } | null | undefined,
  b: { slotId: number; end: SlotCompositeEnd } | null | undefined
): boolean => !!a && !!b && a.slotId === b.slotId && a.end === b.end;

// Only the latest preview may update the panel; older answers are dropped.
let previewSeq = 0;

/**
 * Merge Geometries state. The selection and its validity come from the shared
 * selection and the gun barrel's slots; the panel's options, name, drilling
 * order and totals come from the server's preview.
 *
 * @param {string | null} highlightedFormation - The formation the rail is on.
 * @param {boolean} owner - True for the ONE caller (the gun barrel pane) that
 *   keeps the open draft in step with the selection and the unit.
 * @return {MergeGeometries} Merge state and actions.
 */
export const useMergeGeometries = (
  highlightedFormation: string | null,
  owner = false
): MergeGeometries => {
  const { unitId } = useUnitBuilder();
  const scopedSlotKeys = useAtomValue(scopedSlotKeysAtom);
  const [draft, setDraft] = useAtom(mergeDraftAtom);
  const gunBarrel = useAtomValue(gunBarrelStateAtoms.builder);
  const { preview, create, creating } = useCompositeActions();
  const bumpRefresh = useSetAtom(compositeRefreshTriggerAtom);

  const { slotsById, slotsByLabel } = useMemo(() => {
    const byId = new Map<number, MergeSlot>();
    const byLabel = new Map<string, MergeSlot>();
    const layer = gunBarrel.data?.poiLayers?.find(
      (l: { kind: string }) => l.kind === 'inventory_unit_slots'
    ) as { zones: InventoryUnitSlotsZone[] } | undefined;
    layer?.zones?.forEach((zone) =>
      zone.slots.forEach((s) => {
        const slot: MergeSlot = {
          id: s.id,
          label: s.slotId,
          formation: zone.formationName,
          isPermit: isPermitSlot(s),
          compositeId: s.compositeId ?? null,
        };
        byId.set(s.id, slot);
        byLabel.set(s.slotId, slot);
      })
    );
    return { slotsById: byId, slotsByLabel: byLabel };
  }, [gunBarrel.data]);

  const selected = useMemo(() => {
    const out: MergeSlot[] = [];
    scopedSlotKeys.forEach((key) => {
      if (!key.startsWith('slot:')) return;
      const slot = slotsByLabel.get(key.slice('slot:'.length));
      if (slot) out.push(slot);
    });
    return out;
  }, [scopedSlotKeys, slotsByLabel]);

  const disabledReason = useMemo(() => {
    if (selected.filter((s) => !s.isPermit).length < 2) {
      return 'Select two or more LTD properties with generated geometry';
    }
    if (selected.some((s) => s.isPermit)) return 'Permits cannot be merged';
    if (selected.some((s) => s.compositeId !== null)) {
      return 'A selected LTD is already part of a composite wellbore — split it first';
    }
    return null;
  }, [selected]);

  const hf = highlightedFormation?.toUpperCase() ?? null;
  const otherFormationSlots = selected.filter(
    (s) => hf !== null && s.formation.toUpperCase() !== hf
  );
  const otherFormations = [
    ...new Set(otherFormationSlots.map((s) => s.formation)),
  ];

  // Inherited slots get new ids on the first preview; keep the server's ids
  // (matched by property id) so later previews and the merge send those.
  const idForLabel = useCallback(
    (slot: MergeSlot): number => {
      const leg = draft?.preview?.legs.find((l) => l.propertyId === slot.label);
      return leg?.slotId ?? slot.id;
    },
    [draft?.preview]
  );

  const runPreview = useCallback(
    (next: MergeDraft) => {
      previewSeq += 1;
      const seq = previewSeq;
      const body: SlotCompositeBody = {
        legSlotIds: next.legSlotIds,
        ...(next.landing ? { landingPoint: next.landing } : {}),
        ...(next.bottomHole ? { bottomHole: next.bottomHole } : {}),
      };
      setDraft({ ...next, loading: true });
      preview(body, (composite, error) => {
        if (seq !== previewSeq) return;
        setDraft((prev) => {
          if (!prev) return prev;
          if (!composite) {
            return {
              ...prev,
              loading: false,
              error: error?.message ?? 'Preview failed.',
            };
          }
          const ids = composite.legs.map((l) => l.slotId);
          const sent = new Set(next.legSlotIds);
          return {
            ...prev,
            legSlotIds: ids,
            preview: composite,
            error: null,
            loading: false,
            materialized: prev.materialized || ids.some((id) => !sent.has(id)),
          };
        });
      });
    },
    [preview, setDraft]
  );

  const openMerge = useCallback(() => {
    if (disabledReason) return;
    runPreview({
      legSlotIds: selected.map(idForLabel),
      landing: null,
      bottomHole: null,
      preview: null,
      error: null,
    });
  }, [disabledReason, selected, idForLabel, runPreview]);

  // An inherited unit was copied into this workspace by a preview: refetch so
  // every surface carries the new ids (a merge refreshes on its own).
  const closeMerge = useCallback(() => {
    if (draft?.materialized) bumpRefresh((n) => n + 1);
    setDraft(null);
  }, [draft?.materialized, bumpRefresh, setDraft]);

  // While the panel is open it follows the selection, and closes once the
  // selection can no longer be merged.
  const selectionKey = selected.map((s) => s.label).join(',');
  useEffect(() => {
    if (!owner || !draft) return;
    if (disabledReason) {
      closeMerge();
      return;
    }
    const legSlotIds = selected.map(idForLabel);
    const keep = (p: MergeDraft['landing']) => {
      if (!p || !legSlotIds.includes(p.slotId)) return null;
      return p;
    };
    runPreview({
      ...draft,
      legSlotIds,
      landing: keep(draft.landing),
      bottomHole: null,
    });
    // Re-run only when the selected slots change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey]);

  useEffect(() => {
    if (owner) setDraft(null);
  }, [owner, unitId, setDraft]);

  const landingOptions = useMemo<MergeEndOption[]>(
    () =>
      (draft?.preview?.legs ?? []).flatMap((leg) =>
        leg.ends.map((e) => ({
          key: endKey(leg.slotId, e.end),
          slotId: leg.slotId,
          end: e.end,
          label: `${leg.propertyId} · ${e.label}`,
          coordinates: e.coordinates,
        }))
      ),
    [draft?.preview]
  );

  // The server's bottom-hole options depend on the landing point, so they only
  // count once the latest preview was made with the landing the user picked.
  const previewHasLanding = samePoint(
    draft?.preview?.landingPoint,
    draft?.landing
  );
  const bottomHoleOptions = useMemo<MergeEndOption[]>(() => {
    if (!draft?.landing || !previewHasLanding) return [];
    return (draft.preview?.bottomHoleOptions ?? []).map((o) => {
      const tip = landingOptions.find((l) => l.key === endKey(o.slotId, o.end));
      return {
        key: endKey(o.slotId, o.end),
        slotId: o.slotId,
        end: o.end,
        label: tip?.label ?? `${o.propertyId ?? o.slotId} · ${o.label}`,
        coordinates: tip?.coordinates ?? [],
      };
    });
  }, [draft, previewHasLanding, landingOptions]);

  const previewComposite =
    draft?.landing &&
    draft.bottomHole &&
    samePoint(draft.preview?.landingPoint, draft.landing) &&
    samePoint(draft.preview?.bottomHole, draft.bottomHole)
      ? draft.preview
      : null;

  const chain = useMemo(() => {
    if (!previewComposite) return null;
    const legs = [...previewComposite.legs].sort(
      (a, b) => a.position - b.position
    );
    return {
      rows: legs.map((leg, i) => ({
        slotId: leg.slotId,
        label: leg.propertyId,
        lengthFt: leg.perforatedLateralLengthFt ?? 0,
        tangentInFt:
          i === 0
            ? null
            : (previewComposite.tangents.find((t) => t.toSlotId === leg.slotId)
                ?.lengthFt ?? null),
      })),
      producingLengthFt: previewComposite.producingLengthFt,
      tangentLengthFt: previewComposite.tangentLengthFt,
    };
  }, [previewComposite]);

  const assignEnd = useCallback(
    (role: 'landing' | 'bottomHole', key: string | null) => {
      if (!draft || draft.loading) return;
      const point = key ? parseEndKey(key) : null;
      if (role === 'landing') {
        runPreview({ ...draft, landing: point, bottomHole: null, error: null });
      } else {
        runPreview({ ...draft, bottomHole: point, error: null });
      }
    },
    [draft, runPreview]
  );

  const createComposite = useCallback(() => {
    if (!draft?.landing || !draft.bottomHole || !previewComposite) return;
    const body = {
      legSlotIds: draft.legSlotIds,
      landingPoint: draft.landing,
      bottomHole: draft.bottomHole,
    };
    create(body, (ok, error) => {
      if (ok) {
        setDraft(null);
        return;
      }
      const expected = error?.expectedBottomHole;
      if (expected) {
        runPreview({
          ...draft,
          bottomHole: { slotId: expected.slotId, end: expected.end },
          error: error?.message ?? null,
        });
        return;
      }
      setDraft((prev) => {
        if (!prev) return prev;
        return { ...prev, error: error?.message ?? 'Merge failed.' };
      });
    });
  }, [draft, previewComposite, create, setDraft, runPreview]);

  return {
    disabledReason,
    selectedCount: selected.length,
    otherFormations,
    otherFormationCount: otherFormationSlots.length,
    openMerge,
    closeMerge,
    draft,
    landingOptions,
    bottomHoleOptions,
    landingKey: draft?.landing
      ? endKey(draft.landing.slotId, draft.landing.end)
      : null,
    bottomHoleKey: draft?.bottomHole
      ? endKey(draft.bottomHole.slotId, draft.bottomHole.end)
      : null,
    chain,
    previewComposite,
    assignEnd,
    createComposite,
    creating,
    slotsById,
  };
};
